import Experience from "@folio-2026/experience"
import { LoadResult } from "@folio-2026/resources"
import AnimatedModel from "@folio-2026/shared/animatedmodel"
import assert from "assert"
import { SkeletonUtils } from "three/examples/jsm/Addons.js"
import {
  add,
  cameraPosition,
  color,
  div,
  dot,
  Fn,
  max,
  mul,
  normalize,
  normalWorld,
  positionWorld,
  pow,
  refract,
  texture,
  uniform,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { Object3D } from "three/webgpu"
import { FolderApi } from "tweakpane"

import OuterParticles from "./OuterParticles"

export default class Man {
  experience: Experience
  debugFolder?: FolderApi

  scene: THREE.Object3D
  resources: {
    [key: string]: LoadResult
  }

  envMap: THREE.Texture

  animatedModel!: AnimatedModel
  model!: Object3D<THREE.Object3DEventMap>

  contactScene!: THREE.Texture
  perlin!: THREE.Texture
  fbm!: THREE.Texture
  outerParticles!: OuterParticles

  settings = {
    position: {
      x: 0,
      y: 0,
      z: 0,
    },
    light: {
      fresnelMultiplier: uniform(0.13),
      color: uniform(color("#2f2f2b")),
    },
  }

  constructor({
    scene,
    debugFolder,
    resources,
  }: {
    scene: THREE.Object3D
    debugFolder?: FolderApi
    resources: {
      [key: string]: LoadResult
    }
  }) {
    this.experience = Experience.getInstance()

    this.debugFolder = debugFolder
    this.scene = scene
    this.resources = resources

    this.setup()

    this.setMaterial()

    this.setDebug()
  }

  setup() {
    const manResource = this.resources["man"]
    assert(manResource, "Man resource not found")
    assert(manResource.scene, "Man resource scene not found")

    this.model = SkeletonUtils.clone(manResource.scene as THREE.Group)
    assert(this.model, "Model not found")

    const animations = manResource.animations || []
    this.animatedModel = new AnimatedModel({
      source: this.model,
      animations,
      container: this.scene as THREE.Scene,
      debugFolder: this.debugFolder!,
    })

    this.model.rotation.y = Math.PI

    this.model.position.set(
      this.settings.position.x,
      this.settings.position.y,
      this.settings.position.z,
    )

    this.setAction()

    this.contactScene = this.resources["contact-scene"] as THREE.Texture
    this.envMap = this.contactScene

    this.perlin = this.experience.globalResources.perlin as THREE.Texture
    this.perlin.wrapS = THREE.RepeatWrapping
    this.perlin.wrapT = THREE.RepeatWrapping

    this.fbm = this.experience.globalResources.fbm as THREE.Texture
    this.fbm.wrapS = THREE.RepeatWrapping
    this.fbm.wrapT = THREE.RepeatWrapping

    // Get the SkinnedMesh directly to ensure same reference
    let skinnedMesh: THREE.SkinnedMesh | null = null
    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh && !skinnedMesh) {
        skinnedMesh = child
      }
    })
    assert(skinnedMesh, "SkinnedMesh not found in model")

    this.outerParticles = new OuterParticles({
      scene: this.scene,
      man: skinnedMesh!,
      debugFolder: this.debugFolder,
      fbm: this.fbm,
      perlin: this.perlin,
    })
  }

  setAction() {
    this.animatedModel.startAction("waiting")
  }

  setMaterial() {
    if (!this.model) {
      return
    }

    const material = new THREE.MeshBasicNodeMaterial()
    material.side = THREE.FrontSide

    const viewDir = normalize(positionWorld.sub(cameraPosition))
    const inverseFresnel = max(0, dot(viewDir.mul(-1), normalWorld))
    const fresnel = inverseFresnel.oneMinus()

    const colorShader = Fn(() => {
      const baseColor = vec3(1)
      const light = this.settings.light.color.toVec3()

      const refFresnel = pow(fresnel, 1)
      const refractDirR = normalize(refract(viewDir, normalWorld, div(1, 1.07)))
      const refractDirG = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.2, refFresnel)))),
      )
      const refractDirB = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.6, refFresnel)))),
      )
      const refractColorR = texture(this.envMap, refractDirR).r.mul(0.5)
      const refractColorG = texture(this.envMap, refractDirG).g.mul(0.5)
      const refractColorB = texture(this.envMap, refractDirB).b
      const refractColor = vec3(refractColorR, refractColorG, refractColorB)

      light.addAssign(
        refractColor.mul(fresnel).mul(this.settings.light.fresnelMultiplier),
      )

      const finalColor = baseColor.mul(light)

      return finalColor
    })

    material.colorNode = colorShader()

    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh) {
        child.material = material
      }
    })
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "👤 Man",
      expanded: false,
    })

    const uniforms = {
      fresnelMultiplier: this.settings.light.fresnelMultiplier.value,
    }

    folder
      .addBinding(uniforms, "fresnelMultiplier", {
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.light.fresnelMultiplier.value = ev.value
      })

    // Color control
    const colorSettings = {
      lightColor: `#${new THREE.Color(this.settings.light.color.value).getHexString()}`,
    }

    folder
      .addBinding(colorSettings, "lightColor", {
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.light.color.value = new THREE.Color(ev.value)
      })
  }

  update(params: { delta: number; elapsed: number }) {
    this.animatedModel.update(params)
    this.outerParticles.update()
  }
}
